import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CircleNotch } from "@phosphor-icons/react";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatCurrency, formatDateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  SectionShell,
  StatusPill,
} from "@/components/therapist/settings/section-primitives";

interface PayoutRequestRow {
  id: string;
  amount: number;
  note: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  resolution: "approved" | "rejected" | "withdrawn" | null;
  adminNote: string | null;
  sessionCount: number;
  /** Stvarna isplata za odobren zahtjev; `amount` je iznos u trenutku slanja. */
  paidAmount: number | null;
  paidGrossAmount: number | null;
  paidWithheld: number | null;
}

interface OpenPayoutRequest {
  id: string;
  amount: number;
  note: string | null;
  requestedAt: string;
}

interface PayoutRequestsResponse {
  open: OpenPayoutRequest | null;
  history: PayoutRequestRow[];
  pendingAmount: number;
  minAmount: number | null;
}

export function PayoutRequestPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  const { data, isFetching, isPending, isError } = useQuery({
    queryKey: ["therapist-payout-requests"],
    queryFn: () =>
      apiGet<PayoutRequestsResponse>(
        "/api/therapist/payout-requests",
        t("therapist.finances.errorLoadingPayouts"),
      ),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["therapist-payout-requests"] });
    queryClient.invalidateQueries({ queryKey: ["therapist", "earnings"] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      apiSend(
        "/api/therapist/payout-requests",
        "POST",
        { note: note.trim() || undefined },
        // Rezervna poruka kad server ne vrati vlastitu (njegova se prikazuje u onError).
        "Zahtjev za isplatu nije poslan. Pokušajte ponovo.",
      ),
    onSuccess: () => {
      setNote("");
      setNoteOpen(false);
      toast.success(t("therapist.finances.payoutRequest.created"));
      refresh();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const withdrawMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(
        `/api/therapist/payout-requests/${id}/withdraw`,
        "POST",
        {},
        "Zahtjev nije povučen. Pokušajte ponovo.",
      ),
    onSuccess: () => {
      toast.success(t("therapist.finances.payoutRequest.withdrawn"));
      refresh();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const pendingAmount = data?.pendingAmount ?? 0;
  const minAmount = data?.minAmount ?? null;
  const open = data?.open ?? null;
  const belowMin = minAmount !== null && pendingAmount < minAmount;
  const canRequest = !open && pendingAmount > 0 && !belowMin;

  const resolutionLabel = (row: PayoutRequestRow) => {
    if (row.resolution === "approved") return t("therapist.finances.payoutRequest.resolutionApproved");
    if (row.resolution === "rejected") return t("therapist.finances.payoutRequest.resolutionRejected");
    return t("therapist.finances.payoutRequest.resolutionWithdrawn");
  };

  return (
    <SectionShell
      title={t("therapist.finances.payoutRequest.title")}
      description={
        isPending || isError
          ? t("therapist.finances.payoutRequest.title")
          : `${t("therapist.finances.payoutRequest.pending")}: ${formatCurrency(pendingAmount)}`
      }
    >
      <div className="flex flex-col gap-4">
        {isPending ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <CircleNotch className="size-4 animate-spin" />
            {t("common.loading")}
          </div>
        ) : isError ? (
          <p className="py-4 text-sm text-destructive">
            {t("therapist.finances.payoutRequest.loadError")}
          </p>
        ) : open ? (
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill
              tone="warning"
              label={t("therapist.finances.payoutRequest.sent", { date: formatDateShort(open.requestedAt) })}
            />
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatCurrency(open.amount)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={withdrawMutation.isPending}
              onClick={() => withdrawMutation.mutate(open.id)}
            >
              {withdrawMutation.isPending && <CircleNotch className="size-4 animate-spin" />}
              {t("therapist.finances.payoutRequest.withdraw")}
            </Button>
          </div>
        ) : noteOpen ? (
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-foreground" htmlFor="payout-request-note">
              {t("therapist.finances.payoutRequest.noteLabel")}
            </label>
            <Textarea
              id="payout-request-note"
              value={note}
              maxLength={500}
              placeholder={t("therapist.finances.payoutRequest.notePlaceholder")}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
                {createMutation.isPending && <CircleNotch className="size-4 animate-spin" />}
                {t("therapist.finances.payoutRequest.submit")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNoteOpen(false);
                  setNote("");
                }}
              >
                {t("therapist.finances.payoutRequest.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" disabled={!canRequest || isFetching} onClick={() => setNoteOpen(true)}>
              {t("therapist.finances.payoutRequest.request")}
            </Button>
            {pendingAmount <= 0 && (
              <span className="text-sm text-muted-foreground">
                {t("therapist.finances.payoutRequest.nothingToRequest")}
              </span>
            )}
            {belowMin && pendingAmount > 0 && (
              <span className="text-sm text-muted-foreground">
                {t("therapist.finances.payoutRequest.belowMin", { min: formatCurrency(minAmount!) })}
              </span>
            )}
          </div>
        )}

        {!isPending && !isError && (
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">
              {t("therapist.finances.payoutRequest.history")}
            </p>
            {(data?.history.length ?? 0) === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                {t("therapist.finances.payoutRequest.noHistory")}
              </p>
            ) : (
              <div>
                {data!.history.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center gap-4 border-b border-border/60 py-3 last:border-b-0"
                  >
                    <span className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatDateShort(row.requestedAt)}
                    </span>
                    <span className="w-24 shrink-0 text-sm font-semibold tabular-nums text-foreground">
                      {formatCurrency(row.paidAmount ?? row.amount)}
                      {row.paidAmount != null && row.paidGrossAmount != null && row.paidGrossAmount !== row.paidAmount && (
                        <span className="block text-[11px] font-normal text-muted-foreground">
                          {t("therapist.finances.payoutRequest.grossShort", { amount: formatCurrency(row.paidGrossAmount) })}
                        </span>
                      )}
                    </span>
                    <StatusPill
                      tone={row.resolution === "approved" ? "ready" : row.resolution === "rejected" ? "warning" : "neutral"}
                      label={resolutionLabel(row)}
                      className="shrink-0"
                    />
                    {row.adminNote && (
                      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={row.adminNote}>
                        {row.adminNote}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
}
